/**
 * Streams a single pre-recorded audio clip through the live "/asr" WebSocket for one
 * model, simulating microphone input (real-time playback -> MediaRecorder -> ws.send),
 * exactly like AdvancedTranscriptionTesting.jsx's startLiveTranscription, but driven by
 * a file instead of getUserMedia.
 */

const IDLE_FLUSH_MS = 3000;
const MIN_POST_END_WAIT_MS = 20000;
const SAFETY_TIMEOUT_FLOOR_MS = 15000;

/**
 * @param {Object} params
 * @param {string} params.wsBaseUrl
 * @param {string} params.apiToken
 * @param {string} params.modelId
 * @param {Blob} params.audioBlob
 * @param {() => boolean} [params.isCancelled] - checked periodically to allow aborting early
 * @returns {Promise<{ hypothesisText: string, elapsedMs: number }>}
 */
export function runLiveClipThroughModel({
  wsBaseUrl,
  apiToken,
  modelId,
  audioBlob,
  isCancelled = () => false,
}) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    let latestHypothesis = "";
    let latestBuffer = "";
    let lastMessageAt = Date.now();
    let settled = false;
    let audioEl = null;
    let audioContext = null;
    let mediaRecorder = null;
    let objectUrl = null;
    let safetyTimeoutId = null;
    let cancelCheckIntervalId = null;
    let idleFlushIntervalId = null;

    const cleanup = () => {
      if (safetyTimeoutId) clearTimeout(safetyTimeoutId);
      if (cancelCheckIntervalId) clearInterval(cancelCheckIntervalId);
      if (idleFlushIntervalId) clearInterval(idleFlushIntervalId);
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        try {
          mediaRecorder.stop();
        } catch (e) {
          /* already stopped */
        }
      }
      if (audioContext && audioContext.state !== "closed") {
        audioContext.close().catch(() => {});
      }
      if (audioEl) {
        audioEl.pause();
        audioEl.src = "";
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        try {
          ws.close(1000, "Benchmark clip finished");
        } catch (e) {
          /* already closed */
        }
      }
    };

    const finish = (result, error) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (error) reject(error);
      else resolve(result);
    };

    const params = new URLSearchParams({ token: apiToken, model: modelId });
    const ws = new WebSocket(`${wsBaseUrl}/asr?${params.toString()}`);

    safetyTimeoutId = setTimeout(() => {
      finish(null, new Error(`Timeout: pas de reponse du modele ${modelId}`));
    }, SAFETY_TIMEOUT_FLOOR_MS);

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (
          data.status === "active_transcription" &&
          Array.isArray(data.lines)
        ) {
          lastMessageAt = Date.now();
          latestHypothesis = data.lines
            .map((line) => (line.text || "").trim())
            .filter(Boolean)
            .join(" ");
          latestBuffer = (data.buffer_transcription || "").trim();
        }
      } catch (error) {
        // ignore non-JSON / unrelated messages
      }
    };

    ws.onerror = () => {
      finish(null, new Error(`WebSocket erreur pour le modele ${modelId}`));
    };

    ws.onclose = (event) => {
      if (!settled) {
        finish(
          null,
          new Error(`WebSocket fermé prématurément (code ${event.code})`),
        );
      }
    };

    ws.onopen = async () => {
      try {
        cancelCheckIntervalId = setInterval(() => {
          if (isCancelled()) {
            finish(null, new Error("Benchmark annulé"));
          }
        }, 500);

        objectUrl = URL.createObjectURL(audioBlob);
        audioEl = new Audio();
        audioEl.src = objectUrl;
        await new Promise((res, rej) => {
          audioEl.addEventListener("canplaythrough", res, { once: true });
          audioEl.addEventListener(
            "error",
            () => rej(new Error("Impossible de charger l'audio")),
            { once: true },
          );
        });

        // Route decoded audio into a MediaStream without playing it out loud.
        audioContext = new AudioContext();
        const source = audioContext.createMediaElementSource(audioEl);
        const destination = audioContext.createMediaStreamDestination();
        source.connect(destination);

        mediaRecorder = new MediaRecorder(destination.stream, {
          mimeType: "audio/webm;codecs=opus",
        });
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0 && ws.readyState === WebSocket.OPEN) {
            ws.send(event.data);
          }
        };
        mediaRecorder.start(250);

        // Extend the safety timeout now that we know the real clip duration.
        // Slower-than-realtime models need extra wall-clock time after playback ends
        // to flush their processing backlog, so the ceiling accounts for that too.
        clearTimeout(safetyTimeoutId);
        const durationMs = Number.isFinite(audioEl.duration)
          ? audioEl.duration * 1000
          : 60000;
        const maxPostEndWaitMs = Math.max(
          MIN_POST_END_WAIT_MS,
          durationMs * 0.5,
        );
        safetyTimeoutId = setTimeout(
          () => {
            finish(
              null,
              new Error(`Timeout: pas de reponse du modele ${modelId}`),
            );
          },
          durationMs + maxPostEndWaitMs + SAFETY_TIMEOUT_FLOOR_MS,
        );

        audioEl.addEventListener(
          "ended",
          () => {
            const endedAt = Date.now();
            const finalize = () => {
              clearInterval(idleFlushIntervalId);
              if (mediaRecorder.state !== "inactive") mediaRecorder.stop();
              if (ws.readyState === WebSocket.OPEN) {
                try {
                  ws.send(JSON.stringify({ type: "end_session" }));
                } catch (e) {
                  /* ignore */
                }
              }
              const hypothesisText = [latestHypothesis, latestBuffer]
                .filter(Boolean)
                .join(" ")
                .trim();
              finish({
                hypothesisText,
                elapsedMs: Date.now() - startTime,
              });
            };

            // Wait for the model to finish committing its backlog rather than a fixed
            // delay: flush once idle (no update + nothing pending) or after a cap tied
            // to clip duration, whichever comes first, so slower models aren't cut off.
            idleFlushIntervalId = setInterval(() => {
              const idleFor = Date.now() - lastMessageAt;
              const waitedSinceEnded = Date.now() - endedAt;
              if (
                (idleFor >= IDLE_FLUSH_MS && !latestBuffer) ||
                waitedSinceEnded >= maxPostEndWaitMs
              ) {
                finalize();
              }
            }, 500);
          },
          { once: true },
        );

        await audioEl.play();
      } catch (error) {
        finish(null, error);
      }
    };
  });
}
